import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { EDIT_PAGE_KEY_MAP, useAutoSaveParams } from '@sg/hooks/useAutoSaveParams';

/**
 * Auto-save for the Phase Table (notes-only edit mode).
 * Persists tableData per Revenue/Profit tab while editing Opportunities & Risks / Action & Note.
 */
export const usePhaseTableAutoSave = (
  isEditing: boolean,
  studioBreakdownData: any[],
  editData: any[],
  activeCard: 'Revenue' | 'Profit',
  tableRef: React.RefObject<any>,
  setStudioBreakdownData: (data: any) => void,
  expandAllCategoriesWithChildren?: (data: any[]) => void,
) => {
  const [autoSaveVisible, setAutoSaveVisible] = useState(false);

  const phasePageKey = `${EDIT_PAGE_KEY_MAP.studio_overview_phase_table}_${activeCard.toLowerCase()}`;

  const { getSaveParamsValue, showRestorePrompt, deleteSaveParamsByPageKey } = useAutoSaveParams({
    pageKey: phasePageKey,
  });

  const currentSaveParams = useMemo(() => {
    if (!isEditing) {
      return { tableData: [], activeCard };
    }
    return {
      tableData: editData.length > 0 ? editData : studioBreakdownData,
      activeCard,
    };
  }, [isEditing, editData, studioBreakdownData, activeCard]);

  const initialParamsRef = useRef<{ tableData: any[]; activeCard: 'Revenue' | 'Profit' }>({
    tableData: [],
    activeCard,
  });

  useEffect(() => {
    if (isEditing && studioBreakdownData.length > 0) {
      if (initialParamsRef.current.tableData.length === 0) {
        initialParamsRef.current = {
          tableData: JSON.parse(JSON.stringify(studioBreakdownData)),
          activeCard,
        };
      }
    } else if (!isEditing) {
      initialParamsRef.current = { tableData: [], activeCard };
    }
  }, [isEditing, studioBreakdownData, activeCard]);

  useAutoSaveParams({
    pageKey: phasePageKey,
    initialParams: initialParamsRef.current,
    saveParamsCur: currentSaveParams,
    isBegin: isEditing,
    isChangeValue: true,
  });

  const handlePhaseEdit = useCallback(() => {
    if (showRestorePrompt(phasePageKey)) {
      setAutoSaveVisible(true);
    } else {
      tableRef.current?.handleEdit();
    }
  }, [showRestorePrompt, phasePageKey, tableRef]);

  const handleAutoSaveCancel = useCallback(() => {
    setAutoSaveVisible(false);
    deleteSaveParamsByPageKey(phasePageKey);
    tableRef.current?.handleEdit();
  }, [deleteSaveParamsByPageKey, phasePageKey, tableRef]);

  const handleAutoSaveConfirm = useCallback(() => {
    const saveValue = getSaveParamsValue(phasePageKey) || {};

    setAutoSaveVisible(false);

    if (saveValue.activeCard && saveValue.activeCard !== activeCard) {
      deleteSaveParamsByPageKey(phasePageKey);
      tableRef.current?.handleEdit();
      return;
    }

    if (saveValue.tableData && Array.isArray(saveValue.tableData) && saveValue.tableData.length > 0) {
      setStudioBreakdownData(saveValue.tableData);
    }

    setTimeout(() => {
      tableRef.current?.handleEdit();
      if (expandAllCategoriesWithChildren && saveValue.tableData) {
        expandAllCategoriesWithChildren(saveValue.tableData);
      }
    }, 100);
  }, [
    getSaveParamsValue,
    phasePageKey,
    activeCard,
    deleteSaveParamsByPageKey,
    setStudioBreakdownData,
    tableRef,
    expandAllCategoriesWithChildren,
  ]);

  const clearPhaseAutoSave = useCallback(() => {
    deleteSaveParamsByPageKey(phasePageKey);
  }, [deleteSaveParamsByPageKey, phasePageKey]);

  return {
    autoSaveVisible,
    handlePhaseEdit,
    handleAutoSaveCancel,
    handleAutoSaveConfirm,
    clearPhaseAutoSave,
  };
};
